import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection, type EditorState } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { defaultCaseBrief, defaultCaseBriefJapanese, retiredTemplateLines } from "../const/defaultCaseBrief";

type TPlaceholderMatchers = { whole: Set<string>; labelled: Map<string, string[]> };

const LABELLED = [/^\*\*(.+?):\*\*\s*(.+)$/, /^\*\*(.+?)\*\*:\s*(.+)$/];
const stripMarkdown = (text: string) => text.replace(/\*\*|__/g, "").replace(/\s+/g, " ").trim();

// The template's prompt lines, so they can be told apart from content that replaced them.
export function buildPlaceholderMatchers(templates: string[]): TPlaceholderMatchers {
  const whole = new Set<string>();
  const labelled = new Map<string, string[]>();
  for (const template of templates) {
    for (const raw of template.split("\n")) {
      const line = raw.trim().replace(/^[-*]\s+/, "");
      if (!line || line.startsWith("#")) continue;
      const match = LABELLED.map((pattern) => line.match(pattern)).find(Boolean);
      if (!match) {
        whole.add(stripMarkdown(line));
        continue;
      }
      const label = stripMarkdown(match[1]);
      labelled.set(label, [...(labelled.get(label) ?? []), stripMarkdown(match[2])]);
    }
  }
  return { whole, labelled };
}

// The part of a line that is still template text: the whole line, or the prompt after a label
// ("Decision Question: What decision are you trying to make?"). Offsets are within `text`.
export function placeholderRange(text: string, matchers: TPlaceholderMatchers): [number, number] | null {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  if (matchers.whole.has(normalized)) return [text.indexOf(normalized[0]), text.length];
  for (const [label, prompts] of matchers.labelled) {
    if (!normalized.startsWith(`${label}:`)) continue;
    const prompt = prompts.find((candidate) => normalized.slice(label.length + 1).trim() === candidate);
    if (!prompt) continue;
    const start = text.lastIndexOf(prompt);
    if (start >= 0) return [start, start + prompt.length];
  }
  return null;
}

const matchers = buildPlaceholderMatchers([defaultCaseBrief, defaultCaseBriefJapanese, ...retiredTemplateLines]);

function promptRangeAt(state: EditorState, from: number, to: number): [number, number] | null {
  const $from = state.doc.resolve(from);
  const block = $from.parent;
  if (!block.isTextblock || block.type.name === "heading" || to > $from.end()) return null;
  const range = placeholderRange(block.textContent, matchers);
  if (!range) return null;
  const start = $from.start() + range[0];
  const end = $from.start() + range[1];
  const afterLabel = range[0] > 0 && block.textContent[range[0] - 1] === " " ? start - 1 : start;
  return from < afterLabel || to > end ? null : [start, end];
}

// Shows the brief's untouched template prompts in grey; the text itself is unchanged. Off until
// `storage.enabled` is set, which the editor does only for organizations with document reading.
// `labelClassName`, when set, also styles the label of an untouched "Label: prompt" line.
export const TemplatePlaceholders = Extension.create<
  object,
  { enabled: boolean; className: string; labelClassName: string | null }
>({
  name: "templatePlaceholders",
  addStorage() {
    return { enabled: false, className: "text-innoveraZinc-400", labelClassName: null };
  },
  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin({
        key: new PluginKey("templatePlaceholders"),
        props: {
          handleTextInput(view, from, to, text) {
            const prompt = storage.enabled && !view.composing ? promptRangeAt(view.state, from, to) : null;
            if (!prompt) return false;
            view.dispatch(view.state.tr.insertText(text, prompt[0], prompt[1]));
            return true;
          },
          handleDOMEvents: {
            // Replacing text under an active IME composition breaks it, so the prompt goes first.
            compositionstart(view) {
              const { from, to } = view.state.selection;
              const prompt = storage.enabled ? promptRangeAt(view.state, from, to) : null;
              if (prompt) {
                const tr = view.state.tr.delete(prompt[0], prompt[1]);
                view.dispatch(tr.setSelection(TextSelection.create(tr.doc, prompt[0])));
              }
              return false;
            }
          },
          handlePaste(view, _event, slice) {
            const { from, to } = view.state.selection;
            const prompt = storage.enabled ? promptRangeAt(view.state, from, to) : null;
            if (!prompt) return false;
            view.dispatch(view.state.tr.replaceRange(prompt[0], prompt[1], slice).scrollIntoView());
            return true;
          },
          decorations(state) {
            if (!storage.enabled) return DecorationSet.empty;
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isTextblock || node.type.name === "heading") return true;
              const range = placeholderRange(node.textContent, matchers);
              if (range) {
                decorations.push(Decoration.inline(pos + 1 + range[0], pos + 1 + range[1], { class: storage.className }));
                if (storage.labelClassName && range[0] > 0) {
                  decorations.push(Decoration.inline(pos + 1, pos + 1 + range[0], { class: storage.labelClassName }));
                }
              }
              return false;
            });
            return DecorationSet.create(state.doc, decorations);
          }
        }
      })
    ];
  }
});
